import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
type Project = {
  id: number;
  title: string;
  category: string;
  image: string;
  aspect: string;
  gallery: string[];
  description?: string;
};
const projects: Project[] = [{
  id: 0,
  title: 'InnerJoy — The Missing Piece',
  category: 'Presentation Design',
  image: 'https://cdn.magicpatterns.com/uploads/3mozbM9AFcmEYJqAVLomPu/Screenshot_2026-06-07_at_2.18.03_PM.png',
  aspect: 'aspect-[16/9]',
  description: 'A pitch deck for an SEL education platform — building a confident narrative around the emotional crisis in classrooms and the product designed to solve it.',
  gallery: ['https://cdn.magicpatterns.com/uploads/3mozbM9AFcmEYJqAVLomPu/Screenshot_2026-06-07_at_2.18.03_PM.png', 'https://cdn.magicpatterns.com/uploads/1pazQpP79QHDsa1Kc53oKF/Screenshot_2026-06-07_at_2.18.33_PM.png', 'https://cdn.magicpatterns.com/uploads/9M2MDUx8Em6cNd9GoBakFu/Screenshot_2026-06-07_at_2.18.27_PM.png']
}, {
  id: 5,
  title: 'Binding Arbitration',
  category: 'Presentation Design',
  image: 'https://cdn.magicpatterns.com/uploads/nqYaTVQybKTnZeVmyhmGmy/Screenshot_2026-06-07_at_10.23.57_PM.png',
  aspect: 'aspect-[16/9]',
  description: 'An educational presentation for Pepperdine Graziadio Business School, explaining the binding arbitration process through clear typographic hierarchy and a calm, professional visual system.',
  gallery: ['https://cdn.magicpatterns.com/uploads/nqYaTVQybKTnZeVmyhmGmy/Screenshot_2026-06-07_at_10.23.57_PM.png', 'https://cdn.magicpatterns.com/uploads/2F2ZEy6X2HVriqziLJQrdj/Screenshot_2026-06-07_at_10.24.04_PM.png', 'https://cdn.magicpatterns.com/uploads/okrvBe7WBesBnChedodaos/Screenshot_2026-06-07_at_10.24.10_PM.png', 'https://cdn.magicpatterns.com/uploads/fFyvYD2wfxjWmS1baEs8kZ/Screenshot_2026-06-07_at_10.24.16_PM.png', 'https://cdn.magicpatterns.com/uploads/i86FBhNJvzh442ZQk86snW/Screenshot_2026-06-07_at_10.24.29_PM.png']
}, {
  id: 6,
  title: 'MasterClass — Quarterly Business Review',
  category: 'Presentation Design',
  image: 'https://cdn.magicpatterns.com/uploads/cPLpVxRhMzRFD4MBzjnkEw/MasterClass_-_Final_copy-images-0.jpg',
  aspect: 'aspect-[16/9]',
  description: 'A bold, high-contrast quarterly business review deck for MasterClass — pairing oversized type with the brand\u2019s signature crimson and ink palette to drive a confident internal narrative.',
  gallery: ['https://cdn.magicpatterns.com/uploads/cPLpVxRhMzRFD4MBzjnkEw/MasterClass_-_Final_copy-images-0.jpg', 'https://cdn.magicpatterns.com/uploads/9As3P25wXCywRH6RKdXAqK/MasterClass_-_Final_copy-images-1.jpg', 'https://cdn.magicpatterns.com/uploads/9WFhrRkX7d3qNYNunJLxEZ/MasterClass_-_Final_copy-images-5.jpg', 'https://cdn.magicpatterns.com/uploads/7YGv67gCDrJNqn4rQh1KsH/MasterClass_-_Final_copy-images-6.jpg', 'https://cdn.magicpatterns.com/uploads/c4Cmz4YsFSmYA3Tyvepgaz/MasterClass_-_Final_copy-images-7.jpg']
}, {
  id: 9,
  title: 'Grid — Powering the Intelligent Utility',
  category: 'Presentation Design',
  image: 'https://cdn.magicpatterns.com/uploads/4ky7xZ9UpyyNy6haXgYyeT/Screenshot_2026-06-28_at_11.01.16_PM.png',
  aspect: 'aspect-[16/9]',
  description: 'An investor-facing deck for Grid, an AI-driven operational intelligence platform for utilities — using a crisp, data-forward layout and confident type to frame a billion-dollar market opportunity.',
  gallery: ['https://cdn.magicpatterns.com/uploads/4ky7xZ9UpyyNy6haXgYyeT/Screenshot_2026-06-28_at_11.01.16_PM.png', 'https://cdn.magicpatterns.com/uploads/1gdaFApHWUdKUWHucg7wxV/Screenshot_2026-06-28_at_11.01.22_PM.png']
}, {
  id: 8,
  title: 'Infographics',
  category: 'Infographic Design',
  image: 'https://cdn.magicpatterns.com/uploads/eJrdUsMLFSzg3T8sHU2Gc1/Screenshot_2026-06-07_at_10.50.54_PM.png',
  aspect: 'aspect-[4/3]',
  description: 'Data-driven infographics that turn statistics and processes into clear, engaging visual stories — using color, hierarchy, and illustration to make complex information easy to grasp.',
  gallery: ['https://cdn.magicpatterns.com/uploads/eJrdUsMLFSzg3T8sHU2Gc1/Screenshot_2026-06-07_at_10.50.54_PM.png', 'https://cdn.magicpatterns.com/uploads/nkHqmKU1AN3Qq5fArcPKin/Screenshot_2026-06-07_at_10.50.48_PM.png', 'https://cdn.magicpatterns.com/uploads/tNYNZTwRXguendD2HLsTBj/Info.png']
}];
export function Work() {
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const openProject = (project: Project) => {
    setActiveProject(project);
    setActiveSlide(0);
  };
  const closeProject = () => setActiveProject(null);
  const nextSlide = () => {
    if (!activeProject) return;
    setActiveSlide(s => (s + 1) % activeProject.gallery.length);
  };
  const prevSlide = () => {
    if (!activeProject) return;
    setActiveSlide(s => (s - 1 + activeProject.gallery.length) % activeProject.gallery.length);
  };
  // Keyboard controls + scroll lock when modal is open
  useEffect(() => {
    if (!activeProject) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProject();
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);
  const hasMultiple = activeProject && activeProject.gallery.length > 1;
  return <section id="work" className="py-24 md:py-32 bg-cream" data-id="element-192">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-193">
        <div className="flex justify-between items-end mb-16 md:mb-24" data-id="element-194">
          <h2 className="text-5xl md:text-7xl font-serif tracking-tight" data-id="element-195">
            Selected <span className="italic text-ink-light" data-id="element-196">Work</span>
          </h2>
          <span className="hidden md:block text-ink-light font-medium" data-id="element-197"></span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-198">
          {projects.map((project, index) => <motion.div key={project.id} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.76, 0, 0.24, 1],
          delay: index % 2 === 0 ? 0 : 0.2
        }} className={`group ${index % 2 !== 0 ? 'md:mt-24' : ''}`} data-id="element-199">
              <button type="button" onClick={() => openProject(project)} className="block w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-cream rounded-sm" aria-label={`View ${project.title} project${project.gallery.length > 1 ? `, ${project.gallery.length} slides` : ''}`} data-id="element-200">
                <div className={`relative overflow-hidden bg-cream-dark mb-6 ${project.aspect}`} data-id="element-201">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" data-id="element-202" />
                  <div className="absolute inset-0 bg-ink/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-203" />
                  {project.gallery.length > 1 && <span className="absolute top-4 right-4 bg-ink/80 text-cream text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm" data-id="element-204">
                      {project.gallery.length} slides
                    </span>}
                </div>
                <div className="flex justify-between items-start" data-id="element-205">
                  <div data-id="element-206">
                    <h3 className="text-2xl font-serif mb-1 group-hover:text-accent transition-colors" data-id="element-207">
                      {project.title}
                    </h3>
                    <p className="text-ink-light text-sm" data-id="element-208">{project.category}</p>
                  </div>
                  <div className="w-8 h-8 rounded-full border border-ink/20 flex items-center justify-center group-hover:bg-accent group-hover:border-accent group-hover:text-cream transition-all duration-300 shrink-0 mt-1" data-id="element-209">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-210">
                      <path d="M1 11L11 1M11 1H3.5M11 1V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-211" />
                    </svg>
                  </div>
                </div>
              </button>
            </motion.div>)}
        </div>
      </div>

      {/* Project Lightbox Modal */}
      <AnimatePresence data-id="element-212">
        {activeProject && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-ink/90 backdrop-blur-sm" onClick={closeProject} role="dialog" aria-modal="true" aria-label={`${activeProject.title} project gallery`} data-id="element-213">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} transition={{
          duration: 0.4,
          ease: [0.76, 0, 0.24, 1]
        }} className="relative w-full max-w-5xl max-h-full flex flex-col" onClick={e => e.stopPropagation()} data-id="element-214">
              {/* Header */}
              <div className="flex justify-between items-start mb-4 text-cream" data-id="element-215">
                <div data-id="element-216">
                  <p className="text-accent text-sm font-medium uppercase tracking-wider mb-1" data-id="element-217">
                    {activeProject.category}
                  </p>
                  <h3 className="text-2xl md:text-3xl font-serif" data-id="element-218">
                    {activeProject.title}
                  </h3>
                </div>
                <button type="button" onClick={closeProject} className="w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center hover:bg-cream hover:text-ink transition-colors shrink-0" aria-label="Close project gallery" data-id="element-219">
                  <X size={20} data-id="element-220" />
                </button>
              </div>

              {/* Slide viewer */}
              <div className="relative bg-cream-dark rounded-lg overflow-hidden" data-id="element-221">
                <AnimatePresence mode="wait" data-id="element-222">
                  <motion.img key={activeSlide} src={activeProject.gallery[activeSlide]} alt={`${activeProject.title} — slide ${activeSlide + 1} of ${activeProject.gallery.length}`} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.25
              }} className="w-full max-h-[65vh] object-contain" data-id="element-223" />
                </AnimatePresence>

                {hasMultiple && <>
                    <button type="button" onClick={prevSlide} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ink/70 text-cream flex items-center justify-center hover:bg-accent transition-colors" aria-label="Previous slide" data-id="element-224">
                      <ChevronLeft size={22} data-id="element-225" />
                    </button>
                    <button type="button" onClick={nextSlide} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ink/70 text-cream flex items-center justify-center hover:bg-accent transition-colors" aria-label="Next slide" data-id="element-226">
                      <ChevronRight size={22} data-id="element-227" />
                    </button>
                  </>}
              </div>

              {/* Description + counter */}
              {activeProject.description && <p className="text-cream/70 text-sm mt-4 max-w-2xl" data-id="element-228">
                  {activeProject.description}
                </p>}

              {hasMultiple && <div className="flex items-center justify-between mt-4" data-id="element-229">
                  <div className="flex gap-2" data-id="element-230">
                    {activeProject.gallery.map((thumb, i) => <button key={i} type="button" onClick={() => setActiveSlide(i)} className={`h-14 w-24 rounded overflow-hidden border-2 transition-colors ${i === activeSlide ? 'border-accent' : 'border-transparent opacity-60 hover:opacity-100'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-231">
                        <img src={thumb} alt="" className="w-full h-full object-cover" data-id="element-232" />
                      </button>)}
                  </div>
                  <span className="text-cream/60 text-sm font-medium shrink-0 ml-4" data-id="element-233">
                    {activeSlide + 1} / {activeProject.gallery.length}
                  </span>
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}